import React, { JSX, ReactElement, ReactNode, RefObject, useEffect, useState } from 'react';
import {
  ApiBaseData,
  ApiDurationInfo,
  ApiItemType,
  ApiRoomContainer,
  ApiSearchProductDataResponse,
  ApiSeason
} from '@ibe/api';
import { LoggerFactory } from '@ibe/services';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { Alert, Button, Col, Row } from 'reactstrap';
import { useMount } from 'react-use';
import fallback from '../../../_Translations/generated/PackageSearch.en.t.json';
import Keys from '../../../_Translations/generated/PackageSearch.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import PackageSearchParams from './PackageSearchParams';
import PackageSearchStore from './PackageSearchStore';
import useApi from '../../Hooks/useApi';
import useConfig from '../../Hooks/useConfig';
import isSearchDisabled from './externals';
import useSetInitialFromExternal from './useSetInitialFromExternal';
import BestPriceAvailabilitySearchStore from '../BestPriceAvailabilitySearch/BestPriceAvailabilitySearchStore';
import DestinationItem from '../../../Models/DestinationItem';
import {
  CategorySelect,
  DateMonthPicker,
  DateRangePicker,
  DateRangePickerSingleSelect,
  DestinationSelect,
  IndexedDestinationSelect,
  OriginCheckboxes,
  OriginSelect,
  PaxSelector
} from './SearchComponents';
import { CollapsibleRefFns } from '../SearchCollapsible/SearchCollapsible';
import PackageSearchValidationErrorMessage from './PackageSearchValidationErrorMessage';
import { PaxRoomSelectionImperativeHandleRefType } from '../../Components/UI/PaxRoomSelection/PaxRoomSelection';
import { ChangeType } from '../../Modules/Package/externals';

const logger = LoggerFactory.get('PackageSearch.tsx');
export type DateRangeDurationPropsType = {
  value?: ApiDurationInfo;
  withDuration?: boolean;
  usePlaceholderAsOption?: boolean;
  onDurationChange?: (numberOfNight?: number) => void;
};
export interface Props {
  store: PackageSearchStore;
  searchParams?: Partial<PackageSearchParams>;
  onSubmit?: (values: PackageSearchParams) => void;
  withCategorySelect?: boolean;
  withDateRangePicker?: boolean;
  withAdvancedDateRangePicker?: boolean;
  withChildAgePicker?: boolean;
  childAgeRestriction?: number;
  withRoomCountSelector?: boolean;
  showLabels?: boolean;
  withGroupRequestForm?: boolean;
  subType?: ApiItemType;
  hideOrigin?: boolean;
  hideDestination?: boolean;
  destinationMandatory?: boolean;
  endDateMandatory?: boolean;
  minDateRangeSpan?: number;
  maxDateRangeSpan?: number;
  selectOptions?: ApiBaseData[];
  setSelectOptions?: (options: ApiBaseData[]) => void;
  renderSearch?: (
    CategorySelect: ReactNode,
    OriginSelect: ReactNode,
    DestinationSelect: ReactNode,
    TravelDatePicker: ReactNode,
    TravelMonthPicker: ReactNode,
    TravelerPicker: ReactNode,
    SearchButton: ReactNode,
    OriginCheckboxes: ReactNode,
    DateRangePickerSingleSelect: ReactNode,
    collapsibleRef?: RefObject<CollapsibleRefFns | null>
  ) => ReactElement;
  seasons?: ApiSeason[];
  minSelectableChildAge?: number;
  noDatePickerOverflow?: boolean;
  selectLabelSort?: boolean;
  withChildrenSelection?: boolean | true;
  changeOnBlur?: boolean;
  onlyAdultSelect?: boolean;
  renderDestinationLabel?: (option: DestinationItem, searchTerm?: string) => JSX.Element;
  optionsMapper?: (data: ApiSearchProductDataResponse, searchTerm?: string) => DestinationItem[];
  /** @default true */
  packageOriginMustBeSet?: boolean;
  packageOriginWithRemoveAll?: boolean;
  packageOriginWithSelectAll?: boolean;
  customIndexedDestinationSelect?: (store: PackageSearchStore) => JSX.Element;
  dateRangeDurationProps?: DateRangeDurationPropsType;
  dateRangeFillWithLastDateRangeWhenNoSelection?: boolean;
  dateRangeClearSelectionsOnOpen?: boolean;
  elementsBeforePaxSelector?: Array<JSX.Element>;
  collapsibleRef?: RefObject<CollapsibleRefFns | null>;
  dateRangeUpdateStateFromProps?: boolean;
  showPaxSelectionInModal?: boolean;
  showDateSelectionInModal?: boolean;
  modalLabel?: string;
  paxSelectionRevalidateChildrenOnChangeAfterError?: boolean;
  paxSelectionValidateChildrenPaxSelectionOnLeave?: boolean;
  paxSelectionUpdatePaxOnLeaveWhenValid?: boolean;
  paxSelectionValidateChildrenPaxSelectionOnChange?: boolean;
  paxSelectionUpdatePaxOnChangeWhenValid?: boolean;
  paxRoomSelectionImperativeHandleRef?: PaxRoomSelectionImperativeHandleRefType;
  onPackageSearchChange?: (a: { store?: PackageSearchStore; changeType: ChangeType }) => void;
  childAgePickerHeader?: string;
  useGeoUnitTwoColumnStyling?: boolean;
}

export const SearchButton = observer(function SearchButton(props: {
  store: PackageSearchStore | BestPriceAvailabilitySearchStore;
  onSubmit?: (values: PackageSearchParams) => void;
  withCategorySelect?: boolean;
  hideOrigin?: boolean;
  destinationMandatory?: boolean;
  endDateMandatory?: boolean;
  buttonColor?: 'primary' | 'secondary';
  buttonText?: string;
  originMustBeSet?: boolean;
}): JSX.Element {
  const {
    store,
    onSubmit,
    withCategorySelect,
    hideOrigin,
    destinationMandatory,
    endDateMandatory,
    buttonColor = 'primary',
    buttonText,
    originMustBeSet
  } = props;
  const { t } = useTranslation('PackageSearch', fallback, Keys);

  const submit = onSubmit || ((v): void => logger.log('PackageSearch.onSubmit', v));

  const submitted = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    store.resetDetail();

    const paramsToSubmit = toJS(store.searchParams);

    submit(paramsToSubmit);
  };

  return (
    <Button
      onClick={submitted}
      block
      color={buttonColor}
      type="submit"
      className="iso__service-search__button"
      disabled={isSearchDisabled({
        store,
        destinationMandatory,
        endDateMandatory,
        withCategorySelect,
        hideOrigin,
        originMustBeSet
      })}
    >
      {buttonText || t(Keys.submit)}
    </Button>
  );
});

export const PackageSearchRenderer = observer(function PackageSearchRenderer(
  props: Props & { store: PackageSearchStore }
): JSX.Element {
  const {
    onSubmit,
    withChildAgePicker,
    childAgeRestriction,
    withRoomCountSelector,
    showLabels,
    store,
    withGroupRequestForm = true,
    subType = ApiItemType.PACKAGE,
    hideOrigin,
    hideDestination,
    destinationMandatory,
    endDateMandatory,
    withCategorySelect,
    withDateRangePicker,
    withAdvancedDateRangePicker,
    minDateRangeSpan,
    maxDateRangeSpan,
    selectOptions,
    renderSearch,
    seasons,
    minSelectableChildAge,
    noDatePickerOverflow,
    selectLabelSort,
    withChildrenSelection,
    changeOnBlur,
    onlyAdultSelect,
    renderDestinationLabel,
    optionsMapper,
    packageOriginMustBeSet,
    packageOriginWithRemoveAll,
    packageOriginWithSelectAll,
    customIndexedDestinationSelect,
    dateRangeDurationProps,
    elementsBeforePaxSelector,
    collapsibleRef,
    dateRangeUpdateStateFromProps,
    dateRangeFillWithLastDateRangeWhenNoSelection,
    dateRangeClearSelectionsOnOpen,
    showPaxSelectionInModal,
    showDateSelectionInModal,
    modalLabel,
    paxSelectionRevalidateChildrenOnChangeAfterError,
    paxSelectionValidateChildrenPaxSelectionOnLeave,
    paxSelectionValidateChildrenPaxSelectionOnChange,
    paxSelectionUpdatePaxOnLeaveWhenValid,
    paxSelectionUpdatePaxOnChangeWhenValid,
    paxRoomSelectionImperativeHandleRef,
    onPackageSearchChange,
    childAgePickerHeader,
    useGeoUnitTwoColumnStyling,
    searchParams
  } = props;

  const config = useConfig();

  const [paxSelection, setPaxSelection] = useState<ApiRoomContainer[]>(
    searchParams?.occupancy ?? store.searchParams.occupancy ?? []
  );

  useSetInitialFromExternal(
    (): void => {
      if (onSubmit) {
        onSubmit(toJS(store.searchParams));
      }
    },
    store,
    setPaxSelection,
    subType,
    destinationMandatory,
    endDateMandatory,
    withCategorySelect,
    hideOrigin
  );

  const validationErrorComponent = store.showValidationErrorMessage
    ? (errorKeys?: string[]) => (
        <PackageSearchValidationErrorMessage errorKeys={errorKeys} store={store} />
      )
    : undefined;

  const categories = selectOptions || [];

  useEffect(() => {
    store.setOccupancy(paxSelection);
    if (onPackageSearchChange) {
      onPackageSearchChange({ changeType: 'participants', store });
    }
  }, [paxSelection]);

  useEffect(() => {
    // This will keep the user input rather than just resetting to default, without any information to the user.
    if (!store?.duration && !!dateRangeDurationProps?.value?.duration) {
      store.setDuration(dateRangeDurationProps.value.duration);
    }
  }, [dateRangeDurationProps]);
  if (renderSearch) {
    return renderSearch(
      <CategorySelect categories={categories} store={store} showLabels={showLabels} />,
      <OriginSelect
        store={store}
        onPackageSearchChange={onPackageSearchChange}
        showLabels={showLabels}
        selectLabelSort={selectLabelSort}
      />,
      config.useIndexedGeoUnits ? (
        customIndexedDestinationSelect ? (
          customIndexedDestinationSelect(store)
        ) : (
          <IndexedDestinationSelect
            store={store}
            showLabels={showLabels}
            renderLabel={renderDestinationLabel}
            optionsMapper={optionsMapper}
            onPackageSearchChange={onPackageSearchChange}
          />
        )
      ) : (
        <DestinationSelect
          store={store}
          showLabels={showLabels}
          subType={subType}
          selectLabelSort={selectLabelSort}
        />
      ),
      <DateRangePicker
        store={store}
        showLabels={showLabels}
        seasons={seasons}
        minDateRangeSpan={minDateRangeSpan}
        maxDateRangeSpan={maxDateRangeSpan}
        withDateRangePicker={withDateRangePicker}
        noDatePickerOverflow={noDatePickerOverflow}
        durationProps={dateRangeDurationProps}
        updateStateFromProps={dateRangeUpdateStateFromProps}
        fillWithLastDateRangeWhenNoSelection={dateRangeFillWithLastDateRangeWhenNoSelection}
        clearSelectionsOnOpen={dateRangeClearSelectionsOnOpen}
        showDateSelectionInModal={showDateSelectionInModal}
        onPackageSearchChange={onPackageSearchChange}
        modalLabel={modalLabel}
      />,
      <DateMonthPicker
        withDateRangePicker={withDateRangePicker}
        store={store}
        showLabels={showLabels}
      />,
      <PaxSelector
        paxSelection={paxSelection}
        setPaxSelection={setPaxSelection}
        showLabels={showLabels}
        withCategorySelect={withCategorySelect}
        childAgeRestriction={childAgeRestriction}
        withRoomCountSelector={withRoomCountSelector}
        withChildAgePicker={withChildAgePicker}
        withGroupRequestForm={withGroupRequestForm}
        minSelectableChildAge={minSelectableChildAge}
        withChildrenSelection={withChildrenSelection}
        onlyAdultSelect={onlyAdultSelect}
        additionalElementsBefore={elementsBeforePaxSelector}
        displayInAnotherLayer={showPaxSelectionInModal}
        validationErrorComponent={validationErrorComponent}
        revalidateChildrenOnChangeAfterError={paxSelectionRevalidateChildrenOnChangeAfterError}
        validateChildrenPaxSelectionOnLeave={paxSelectionValidateChildrenPaxSelectionOnLeave}
        validateChildrenPaxSelectionOnChange={paxSelectionValidateChildrenPaxSelectionOnChange}
        updatePaxOnChangeWhenValid={paxSelectionUpdatePaxOnChangeWhenValid}
        updatePaxOnLeaveWhenValid={paxSelectionUpdatePaxOnLeaveWhenValid}
        paxRoomSelectionImperativeHandleRef={paxRoomSelectionImperativeHandleRef}
        childAgePickerHeader={childAgePickerHeader}
      />,
      <SearchButton
        withCategorySelect={withCategorySelect}
        store={store}
        hideOrigin={hideOrigin}
        destinationMandatory={destinationMandatory}
        endDateMandatory={endDateMandatory}
        onSubmit={onSubmit}
        originMustBeSet={packageOriginMustBeSet}
      />,
      <OriginCheckboxes
        store={store}
        showLabels={showLabels}
        changeOnBlur={changeOnBlur}
        originMustBeSet={packageOriginMustBeSet}
        showPseudo
        withRemoveAll={packageOriginWithRemoveAll}
        withSelectAll={packageOriginWithSelectAll}
        onPackageSearchChange={onPackageSearchChange}
        useGeoUnitTwoColumnStyling={useGeoUnitTwoColumnStyling}
      />,
      <DateRangePickerSingleSelect
        store={store}
        durationProps={dateRangeDurationProps}
        showLabels={showLabels}
      />,
      collapsibleRef
    );
  }

  return (
    <>
      <Row className="iso__service-search justify-content-center">
        {withCategorySelect && (
          <Col sm={6} lg={2} className="pb-1">
            <CategorySelect categories={categories} store={store} showLabels={showLabels} />
          </Col>
        )}
        {!hideOrigin && !withCategorySelect && (
          <Col sm={6} lg={2} className="pb-1">
            <OriginSelect
              onPackageSearchChange={onPackageSearchChange}
              store={store}
              showLabels={showLabels}
              selectLabelSort={selectLabelSort}
            />
          </Col>
        )}
        {!withCategorySelect && !hideDestination && (
          <Col sm={6} lg={2} className="pb-1">
            <DestinationSelect
              store={store}
              showLabels={showLabels}
              subType={subType}
              selectLabelSort={selectLabelSort}
            />
          </Col>
        )}
        <Col sm={6} lg={3} className="pb-1">
          {!withDateRangePicker && !withAdvancedDateRangePicker && (
            <DateMonthPicker
              withDateRangePicker={withDateRangePicker}
              store={store}
              showLabels={showLabels}
            />
          )}
          {withDateRangePicker && !withAdvancedDateRangePicker && (
            <DateRangePicker
              store={store}
              showLabels={showLabels}
              seasons={seasons}
              minDateRangeSpan={minDateRangeSpan}
              maxDateRangeSpan={maxDateRangeSpan}
              withDateRangePicker={withDateRangePicker}
              noDatePickerOverflow={noDatePickerOverflow}
              durationProps={dateRangeDurationProps}
              updateStateFromProps={dateRangeUpdateStateFromProps}
              fillWithLastDateRangeWhenNoSelection={dateRangeFillWithLastDateRangeWhenNoSelection}
              clearSelectionsOnOpen={dateRangeClearSelectionsOnOpen}
              onPackageSearchChange={onPackageSearchChange}
            />
          )}
          {withAdvancedDateRangePicker && !withDateRangePicker && (
            <DateRangePickerSingleSelect
              store={store}
              durationProps={dateRangeDurationProps}
              showLabels={showLabels}
            />
          )}
        </Col>
        <Col sm={6} lg={3} className="pb-1">
          <PaxSelector
            paxSelection={paxSelection}
            setPaxSelection={setPaxSelection}
            showLabels={showLabels}
            withCategorySelect={withCategorySelect}
            childAgeRestriction={childAgeRestriction}
            withRoomCountSelector={withRoomCountSelector}
            withChildAgePicker={withChildAgePicker}
            withGroupRequestForm={withGroupRequestForm}
            minSelectableChildAge={minSelectableChildAge}
            onlyAdultSelect={onlyAdultSelect}
            revalidateChildrenOnChangeAfterError={paxSelectionRevalidateChildrenOnChangeAfterError}
            validateChildrenPaxSelectionOnLeave={paxSelectionValidateChildrenPaxSelectionOnLeave}
            validateChildrenPaxSelectionOnChange={paxSelectionValidateChildrenPaxSelectionOnChange}
            updatePaxOnChangeWhenValid={paxSelectionUpdatePaxOnChangeWhenValid}
            updatePaxOnLeaveWhenValid={paxSelectionUpdatePaxOnLeaveWhenValid}
            paxRoomSelectionImperativeHandleRef={paxRoomSelectionImperativeHandleRef}
            childAgePickerHeader={childAgePickerHeader}
          />
        </Col>
        <Col
          sm={6}
          lg={2}
          className={`mt-3 mt-sm-0 ${showLabels ? 'align-self-end pb-1' : 'pb-1'}`}
        >
          <SearchButton
            withCategorySelect={withCategorySelect}
            store={store}
            hideOrigin={hideOrigin}
            onSubmit={onSubmit}
            originMustBeSet={packageOriginMustBeSet}
          />
        </Col>
      </Row>

      {store.error && (
        <Alert color="danger" className="mb-0">
          {store.error}
        </Alert>
      )}
    </>
  );
});

export const PackageSearch = observer(function PackageSearch(props: Props): JSX.Element {
  const api = useApi();
  const [types, setTypes] = useState<ApiBaseData[]>([]);

  const {
    searchParams,
    onSubmit,
    withChildAgePicker,
    childAgeRestriction,
    withRoomCountSelector,
    showLabels,
    hideOrigin,
    hideDestination,
    destinationMandatory,
    endDateMandatory,
    withDateRangePicker,
    withAdvancedDateRangePicker,
    withCategorySelect,
    minDateRangeSpan,
    maxDateRangeSpan,
    selectOptions,
    subType = ApiItemType.PACKAGE,
    setSelectOptions,
    withGroupRequestForm,
    renderSearch,
    minSelectableChildAge,
    noDatePickerOverflow,
    selectLabelSort,
    withChildrenSelection,
    changeOnBlur,
    onlyAdultSelect,
    store,
    renderDestinationLabel,
    optionsMapper,
    packageOriginMustBeSet = true,
    packageOriginWithRemoveAll,
    packageOriginWithSelectAll,
    customIndexedDestinationSelect,
    dateRangeDurationProps,
    elementsBeforePaxSelector,
    collapsibleRef,
    dateRangeUpdateStateFromProps,
    dateRangeFillWithLastDateRangeWhenNoSelection,
    dateRangeClearSelectionsOnOpen,
    showPaxSelectionInModal,
    showDateSelectionInModal,
    modalLabel,
    paxSelectionRevalidateChildrenOnChangeAfterError,
    paxSelectionValidateChildrenPaxSelectionOnLeave,
    paxSelectionValidateChildrenPaxSelectionOnChange,
    paxSelectionUpdatePaxOnLeaveWhenValid,
    paxSelectionUpdatePaxOnChangeWhenValid,
    paxRoomSelectionImperativeHandleRef,
    onPackageSearchChange,
    childAgePickerHeader,
    useGeoUnitTwoColumnStyling
  } = props;

  useMount(() => {
    if (withCategorySelect && !selectOptions) {
      api.getServiceGroups(subType).then(response => {
        setTypes(response);
        if (setSelectOptions) {
          setSelectOptions(response);
        }
      });
    }
  });

  useEffect(() => {
    store.setParams(searchParams ?? {});
    store.setParams({ subType });
  }, [searchParams, subType, store]);

  return (
    <PackageSearchRenderer
      searchParams={searchParams}
      onSubmit={onSubmit}
      withChildAgePicker={withChildAgePicker}
      withRoomCountSelector={withRoomCountSelector}
      childAgeRestriction={childAgeRestriction}
      showLabels={showLabels}
      store={store}
      hideOrigin={hideOrigin}
      hideDestination={hideDestination}
      destinationMandatory={destinationMandatory}
      endDateMandatory={endDateMandatory}
      withDateRangePicker={withDateRangePicker}
      withAdvancedDateRangePicker={withAdvancedDateRangePicker}
      withCategorySelect={withCategorySelect}
      minDateRangeSpan={minDateRangeSpan}
      maxDateRangeSpan={maxDateRangeSpan}
      selectOptions={selectOptions || types}
      subType={subType}
      withGroupRequestForm={withGroupRequestForm}
      renderSearch={renderSearch}
      minSelectableChildAge={minSelectableChildAge}
      noDatePickerOverflow={noDatePickerOverflow}
      selectLabelSort={selectLabelSort}
      withChildrenSelection={withChildrenSelection}
      changeOnBlur={changeOnBlur}
      onlyAdultSelect={onlyAdultSelect}
      renderDestinationLabel={renderDestinationLabel}
      optionsMapper={optionsMapper}
      packageOriginMustBeSet={packageOriginMustBeSet}
      packageOriginWithRemoveAll={packageOriginWithRemoveAll}
      packageOriginWithSelectAll={packageOriginWithSelectAll}
      customIndexedDestinationSelect={customIndexedDestinationSelect}
      dateRangeDurationProps={dateRangeDurationProps}
      elementsBeforePaxSelector={elementsBeforePaxSelector}
      collapsibleRef={collapsibleRef}
      dateRangeUpdateStateFromProps={dateRangeUpdateStateFromProps}
      dateRangeFillWithLastDateRangeWhenNoSelection={dateRangeFillWithLastDateRangeWhenNoSelection}
      dateRangeClearSelectionsOnOpen={dateRangeClearSelectionsOnOpen}
      showPaxSelectionInModal={showPaxSelectionInModal}
      showDateSelectionInModal={showDateSelectionInModal}
      modalLabel={modalLabel}
      paxSelectionRevalidateChildrenOnChangeAfterError={
        paxSelectionRevalidateChildrenOnChangeAfterError
      }
      paxSelectionValidateChildrenPaxSelectionOnLeave={
        paxSelectionValidateChildrenPaxSelectionOnLeave
      }
      paxSelectionUpdatePaxOnChangeWhenValid={paxSelectionUpdatePaxOnChangeWhenValid}
      paxSelectionValidateChildrenPaxSelectionOnChange={
        paxSelectionValidateChildrenPaxSelectionOnChange
      }
      paxSelectionUpdatePaxOnLeaveWhenValid={paxSelectionUpdatePaxOnLeaveWhenValid}
      paxRoomSelectionImperativeHandleRef={paxRoomSelectionImperativeHandleRef}
      onPackageSearchChange={onPackageSearchChange}
      childAgePickerHeader={childAgePickerHeader}
      useGeoUnitTwoColumnStyling={useGeoUnitTwoColumnStyling}
    />
  );
});
